import React, { useState } from "react";
import { toast } from "sonner";

const Stat = ({ label, value, color, testId }) => (
    <div>
        <div className={`text-[10px] font-stencil tracking-widest ${color}`}>{label}</div>
        <div className="font-display text-3xl leading-none mt-1" data-testid={testId}>{value}</div>
    </div>
);

export const JournalGiftBlastPanel = ({ authedApi }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testName, setTestName] = useState("");
    const [testLoading, setTestLoading] = useState(false);

    const run = async (dryRun) => {
        setLoading(true);
        try {
            const r = await authedApi.post("/admin/journal-gift/blast", { dry_run: dryRun });
            if (dryRun) {
                setPreview(r.data);
                toast.success(`Dry run complete — ${r.data.eligible} people will get the Journal email.`);
            } else {
                toast.success(`Journal email queued for ${r.data.queued} people · sends within ~5 minutes`);
                setPreview(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Journal blast failed.");
        } finally {
            setLoading(false);
        }
    };

    const fire = () => {
        if (!preview) return;
        if (!window.confirm(
            `SEND the Journal email to ${preview.eligible} people?\n\n` +
            `• One email, sent once. Nobody gets it twice.\n` +
            `• Unsubscribes are excluded.\n\nThis cannot be undone. Continue?`
        )) return;
        run(false);
    };

    const sendTest = async () => {
        setTestLoading(true);
        try {
            const r = await authedApi.post("/admin/journal-gift/test-send", { email: testEmail, first_name: testName });
            toast.success(`Test sent to ${r.data.sent_to}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Test send failed.");
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <div className="mb-8 border-2 border-amber-500/60 bg-gradient-to-br from-amber-950/30 via-black to-black p-6" data-testid="journal-gift-panel">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[280px]">
                    <div className="font-stencil text-[10px] tracking-[0.3em] text-amber-400 mb-2">[ CAMPAIGN LAUNCHER · ONE-TIME JOURNAL EMAIL ]</div>
                    <h3 className="font-display text-2xl uppercase tracking-tight leading-none mb-2">📖 Journal Gift Blast — 1 email</h3>
                    <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
                        &ldquo;The gift that won&rsquo;t end up in a closet by January.&rdquo; One email promoting the Daily Battle Plan journal ($12.99 on Amazon) for yourself or as a gift. Every Journal click is counted in the Click Tracker below. Unsubscribes excluded.
                    </p>
                </div>
                <div className="flex flex-col gap-2">
                    <button onClick={() => run(true)} disabled={loading} className="btn-ghost text-xs" data-testid="journal-gift-dryrun">
                        {loading && !preview ? "Checking…" : "1. Dry-run preview"}
                    </button>
                    <button
                        onClick={fire}
                        disabled={loading || !preview}
                        className="text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase bg-amber-500 text-black"
                        data-testid="journal-gift-fire"
                    >
                        {loading && preview ? "Sending…" : "2. Send the Journal email"}
                    </button>
                </div>
            </div>
            {preview && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-5" data-testid="journal-gift-preview">
                    <Stat label="TOTAL LEADS" value={preview.total_leads_gathered} color="text-white/40" testId="journal-gift-total" />
                    <Stat label="ELIGIBLE" value={preview.eligible} color="text-green-500" testId="journal-gift-eligible" />
                    <Stat label="UNSUB SKIPPED" value={preview.unsubs_excluded} color="text-yellow-500" testId="journal-gift-unsubs" />
                    <Stat label="ALREADY SENT" value={preview.already_sent_excluded} color="text-red-500" testId="journal-gift-already" />
                </div>
            )}
            <div className="mt-6 border-t border-white/10 pt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                <input type="email" placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                    className="md:col-span-2 bg-black border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-amber-500" data-testid="journal-gift-test-email" />
                <input type="text" placeholder="First name (optional)" value={testName} onChange={(e) => setTestName(e.target.value)}
                    className="bg-black border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-amber-500" data-testid="journal-gift-test-firstname" />
                <button onClick={sendTest} disabled={testLoading || !testEmail.includes("@")}
                    className="text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black transition-colors" data-testid="journal-gift-test-send">
                    {testLoading ? "Sending…" : "📧 Send test"}
                </button>
            </div>
        </div>
    );
};
